import { logger } from '@/lib/logger';

/**
 * Reusable function to paginate Supabase queries that may return more than 1000 items.
 * 
 * Supabase has a maximum limit of 1000 items per query. This function automatically
 * handles pagination by continuing to fetch pages while exactly 1000 items are returned,
 * and stopping when fewer than 1000 items are returned (indicating the last page).
 * 
 * @template T - The type of data being fetched
 * @param queryBuilder - A function that receives a range (from, to) and returns a configured Supabase query
 * @param options - Optional configuration
 * @param options.pageSize - Maximum items per page (default: 1000, Supabase's limit)
 * @param options.max - Optional maximum total items to fetch. If provided, pagination stops once this limit is reached.
 * @param options.logContext - Optional context for logging (e.g., 'fetching invoices')
 * @returns Promise<T[]> - All items from all pages combined (up to max if specified)
 * 
 * @example
 * ```typescript
 * // Fetch all items
 * const allInvoices = await paginateSupabaseQuery((from, to) =>
 *   supabase
 *     .from('invoices')
 *     .select('*, invoice_items(*)')
 *     .eq('store_id', storeId)
 *     .order('creation_time', { ascending: false })
 *     .range(from, to)
 * );
 * 
 * // Fetch only first 500 items
 * const first500 = await paginateSupabaseQuery((from, to) =>
 *   supabase
 *     .from('invoices')
 *     .select('*')
 *     .eq('store_id', storeId)
 *     .range(from, to),
 *   { max: 500 }
 * );
 * ```
 */
export async function paginateSupabaseQuery<T = any>(
  queryBuilder: (from: number, to: number) => any,
  options: {
    pageSize?: number;
    max?: number;
    logContext?: string;
  } = {}
): Promise<T[]> {
  const { pageSize = 1000, max, logContext } = options;
  const allItems: T[] = [];
  let page = 0;
  let hasMore = true;

  logger.info('Starting paginated query', { logContext, pageSize, max });

  while (hasMore) {
    const from = page * pageSize;
    
    // If max is specified, adjust the 'to' range to not exceed max
    let to = (page + 1) * pageSize - 1;
    if (max !== undefined) {
      const maxTo = max - 1;
      if (to > maxTo) {
        to = maxTo;
      }
      // If we've already reached max, stop
      if (allItems.length >= max) {
        break;
      }
    }
    
    try {
      const query = queryBuilder(from, to);
      const { data, error } = await query;

      if (error) {
        logger.error('Error in paginated query', { error, page, logContext });
        throw error;
      }

      if (!data) {
        logger.warn('No data returned in paginated query', { page, logContext });
        break;
      }

      // If max is specified, only add items up to the max limit
      if (max !== undefined) {
        const remaining = max - allItems.length;
        if (remaining <= 0) {
          break;
        }
        if (data.length > remaining) {
          allItems.push(...(data.slice(0, remaining) as T[]));
          hasMore = false; // Reached max, stop
        } else {
          allItems.push(...(data as T[]));
          // If we got fewer than pageSize items, we've reached the last page
          hasMore = data.length === pageSize;
        }
      } else {
        // Add all items if no max specified
        allItems.push(...(data as T[]));
        // If we got fewer than pageSize items, we've reached the last page
        hasMore = data.length === pageSize;
      }
      
      logger.debug('Paginated query page fetched', {
        page,
        itemsInPage: data.length,
        totalItems: allItems.length,
        hasMore,
        max,
        logContext
      });

      page++;
    } catch (error) {
      logger.error('Error fetching paginated query page', { error, page, logContext });
      throw error;
    }
  }

  logger.info('Paginated query completed', {
    totalPages: page,
    totalItems: allItems.length,
    max,
    logContext
  });

  return allItems;
}

/**
 * Helper to create a reusable query builder function that can be passed to paginateSupabaseQuery.
 * This is useful when you want to reuse the same query logic multiple times.
 * 
 * @param baseQuery - A Supabase query builder (from().select().where() etc.) without range/limit
 * @returns A function that accepts (from, to) and returns the query with range applied
 * 
 * @example
 * ```typescript
 * const baseQuery = supabase
 *   .from('invoices')
 *   .select('*, invoice_items(*)')
 *   .eq('store_id', storeId)
 *   .order('creation_time', { ascending: false });
 * 
 * const queryBuilder = createPaginatedQueryBuilder(baseQuery);
 * const allInvoices = await paginateSupabaseQuery(queryBuilder, { logContext: 'fetching invoices' });
 * ```
 */
export function createPaginatedQueryBuilder(
  baseQuery: { range: (from: number, to: number) => any }
): (from: number, to: number) => any {
  return (from: number, to: number) => {
    return baseQuery.range(from, to);
  };
}

